import { useRef } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Paper, Avatar, BoxProps, Typography, CardHeader, Grid, Chip } from '@mui/material';
// components
import Label from '../../../../components/label';
import Image from '../../../../components/image';
import Carousel, { CarouselArrows } from '../../../../components/carousel';

// ----------------------------------------------------------------------

type IProduct = {
  id: string;
  name: string;
  description: string;
  date: Date | string | number;
  price: number;
  calorie?: number;
  pictures: any;
  [key: string]: any;
};

interface Props extends BoxProps {
  title?: string;
  subheader?: string;
  list: any;
}

export default function DriveThruProductsDisplay({ title = "Les plus commandés", list, sx, ...other }: Props) {
  const theme = useTheme();

  return (
    <Box sx={{
      p: 3,
      minHeight: '100vh',
      ...sx
    }} {...other}>
      {/* Header */}
      {title && (
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 700,
            color: '#f1223f',
            letterSpacing: 2
          }}
        >
          {title}
        </Typography>
      )}

      {/* Products Grid */}
      <Grid container spacing={2} sx={{ maxWidth: 1200, mx: 'auto' }}>
        {list.slice(0, 16).map((item: any) => (
          <Grid item xs={12} sm={3} md={3} key={item.id}>
            <ProductItem item={item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

type ProductItemProps = {
  item: IProduct;
};

function ProductItem({ item }: ProductItemProps) {
  const { id, name, description, price, trioPrice, calorie, pictures } = item;

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'white',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #f0f0f0'
      }}
    >
      {/* Product Image */}
      <Box sx={{ position: 'relative', height: 200 }}>
        <Image
          alt={name}
          src={`/api/v1/products/${id}/image`}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>

      {/* Product Info */}
      <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, color: '#2c2c2c', fontSize: '1.1rem', lineHeight: 1.3 }}
        >
          {name}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: '#666', fontSize: '0.9rem', mb: 1, minHeight: 48 }}
        >
          {description}
        </Typography>

        <Box sx={{ mt: 'auto' }}>
          {/* Calories */}
          {calorie && (
            <Typography
              variant="body2"
              sx={{ color: '#666', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}
            >
              {calorie} Calos
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: 1,
                py: 0.5,
                fontSize: '0.7rem',
                fontWeight: 700,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              SOLO
              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                ${price.toFixed(2)}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                bgcolor: '#1976d2',
                color: '#fff',
                borderRadius: 1,
                py: 0.5,
                fontSize: '0.7rem',
                fontWeight: 700,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              TRIO
              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                ${trioPrice.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
